import { FieldValues } from "react-hook-form";
import { BatchOperation, OperationEnum } from "../types/api/generic.type";

type GenericWithId<T> = T & {
  id: number;
};

export function compareDataGeneric<T>(
  inputData: GenericWithId<T>[],
  comparisonData: FieldValues,
  excludeFields: (keyof T)[] = [],
): BatchOperation<T>[] {
  const result: BatchOperation<T>[] = [];
  const excludedKeys = new Set<keyof T | "id">([...excludeFields, "id"]);

  const diffLength = comparisonData.length - inputData.length;

  if (diffLength > 0) {
    const newItems: GenericWithId<T>[] = comparisonData.slice(-diffLength);
    newItems.forEach((item) => {
      // eslint-disable-next-line
      const { id, ...rest } = item;

      result.push({
        operation: OperationEnum.ADD,
        entityId: null,
        data: rest as Partial<T>,
      });
    });
  }

  inputData.forEach((item, index) => {
    const compareItem = comparisonData[index];
    if (compareItem) {
      const diff = Object.keys(item).filter(
        (key) =>
          !excludedKeys.has(key as keyof T) &&
          JSON.stringify(item[key as keyof T]) !==
            JSON.stringify(compareItem[key as keyof T]),
      );
      if (diff.length > 0) {
        result.push({
          operation: OperationEnum.UPDATE,
          entityId: item.id,
          data: diff.reduce((acc, key) => {
            acc[key as keyof T] = compareItem[key as keyof T];
            return acc;
          }, {} as T),
        });
      }
    } else {
      result.push({
        operation: OperationEnum.DELETE,
        entityId: item.id,
        data: null,
      });
    }
  });
  return result;
}

export const compareIdsOfTwoArrays = <T>(
  original: GenericWithId<T>[],
  updated: GenericWithId<T>[],
): BatchOperation<T>[] => {
  const originalIds = new Set(original.map((e) => e.id));
  const updatedIds = new Set(updated.map((e) => e.id));

  const deleted: BatchOperation<T>[] = original
    .filter((e) => !updatedIds.has(e.id))
    .map((e) => ({
      operation: OperationEnum.DELETE,
      entityId: e.id,
      data: null,
    }));

  const added: BatchOperation<T>[] = updated
    .filter((e) => !originalIds.has(e.id))
    .map((e) => ({
      operation: OperationEnum.ADD,
      entityId: e.id,
      data: null,
    }));

  return [...deleted, ...added];
};
